<template>
  <v-card class="d-flex overflow-hidden max-height wrap" style="flex-direction: column;">
    <PageHeader no-padding style="flex: 0 0!important;" />
    <v-layout style="flex: 1 0;" class="overflow-hidden">
      <ExecutionResultsDialog title="Fetching import events" ref="executionResultsDialog" />
      <IngestionResultsLogDialog ref="ingestionResultsDialog" />
      <v-navigation-drawer class="card-bg-2 hf-scrollbar-light" left permanent clipped>
        <v-card-text>
          <v-list dense class="transparent">
            <div class="headline align-center">
              <v-icon medium>filter_alt</v-icon>
              Filters
              <SecondaryBtn class="mr-1" style="min-width: 0;" small @click="clearFilters()">CLEAR</SecondaryBtn>
              <SecondaryBtn class="mx-0" no-icon-padding style="min-width: 0;" small @click="refreshResults()" icon="refresh" />
            </div>
            <v-subheader>Event dates</v-subheader>
            <DateRange v-model="queryFilters.dateRange" ref="dateRange" hide-clear :shortcuts="dateRangeShortcuts" rounded solo :min-max-placeholder="formats.prettyDateFormat" no-arrows :numColumns="1" twoLine />
            <div class="text-xs-center">
              <v-btn @click="$refs.dateRange.prevRange()">
                <v-icon color="success">arrow_back</v-icon>
              </v-btn>
              <v-btn @click="$refs.dateRange.nextRange()">
                <v-icon color="success">arrow_forward</v-icon>
              </v-btn>
            </div>
            <v-divider class="my-2" />
            <v-subheader>Event type and outcome status</v-subheader>
            <v-list-tile v-for="(item, i) in statusTypes" :key="i">
              <div class="body-1 align-center d-flex" style="flex: 0 0 115px;">
                <v-icon class="mr-1" v-html="item.icon" />
                {{ item.label }}:
                <v-spacer />
              </div>
              <v-btn-toggle v-model="filters[item.id]" multiple>
                <v-btn value="pass" small>
                  <v-icon color="success">check_circle</v-icon>
                </v-btn>
                <v-btn value="fail" small>
                  <v-icon color="error">error</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-list-tile>
            <v-divider class="my-2" />
            <v-subheader>
              File types used
            </v-subheader>
            <v-list-tile class="pa-0" v-for="(item, index) in dataImporters" :key="`filter-service-${index}`">
              <v-checkbox class="my-0" color="success" hide-details v-model="filters.services" :label="item.name" :value="item.serviceLink" />
            </v-list-tile>
            <v-subheader>
              Other filters
            </v-subheader>
            <v-list-tile class="pa-0 mb-2">
              <v-checkbox class="my-0" color="success" hide-details v-model="otherFilters.latestVersionOnly" label="Only show the latest import versions" />
            </v-list-tile>
            <v-list-tile class="pa-0">
              <v-checkbox class="my-0" color="success" hide-details v-model="otherFilters.skipRolledBackIngestions" label="Don't show ingestions that were rolled back" />
            </v-list-tile>
          </v-list>
        </v-card-text>
      </v-navigation-drawer>
      <v-flex class="overflow-hidden max-height card-bg-1" style="position: relative;">
        <div class="mb-1 overflow-hidden max-height position-relative">
          <Subheader color="#f3f3f3" flat style="flex: 0 0;" light lightSearch v-model="queryFilters.searchText" rounded search="import events">
            <template #tooltip>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-icon class="clickable" color="info" v-on="on">info_outline</v-icon>
                </template>
                <span> Use the filename, COC code or any other field to search. </span>
              </v-tooltip>
            </template>
          </Subheader>
          <v-divider />
          <PageLoader class="mt-5" v-if="loading.ingestionResults" resource-name="import results" />
          <NoResultsFound v-else-if="filteredImportIds.length == 0" text="No import events found. <br/> Expand your date range or clear some filters." />
          <div v-else class="overflow-auto" style="height: Calc(100% - 64px);">
            <v-subheader>
              <div class="mr-1">{{ filteredImportIds.length }} {{ pluralize('import event', filteredImportIds.length) }} found.</div>
              <v-chip small class="body-1" v-for="(importCount, key) in importCounts" :key="`importCount-${key}`">
                {{ importCount }} {{ pluralize(statusTypes[key].label, importCount) }}
              </v-chip>
            </v-subheader>
            <ImportCard v-for="(indexId, j) in filteredImportIds" @show:logs="showIngestionLogs(indexId)" :key="`import-${j}`" :indexId="indexId" />
            <v-divider />
            <div class="text-xs-center py-4">
              End of results
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
  import moment from 'moment'

  import { getMediaTypeIcon } from '@/helpers/mediaTypes'
  import { pluralize } from '@/helpers/strings'
  import { dataImporters } from '@/helpers/datasets/ingestions'
  import { debounce } from '@/helpers/performance'
  import { addParamsToLocation } from '@/helpers/queryStrings'
  import {
    prettyDateFormat,
    backendDateTimeFormat,
    prettyDateTimeFormatShort,
  } from '@/components/mixins/MomentUtil'

  import virtualList from 'vue-virtual-scroll-list'
  import ImportCard from './/ImportCard'
  import IngestionResultsLogDialog from './IngestionResultsLogDialog'

  import { createNamespacedHelpers } from 'vuex'
  const ingestions = createNamespacedHelpers('ingestions')

  const statusTypes = {
    ingestion: {
      label: 'Ingestion',
      id: 'ingestion',
      key: 'isIngestion',
      icon: 'cloud_upload',
    },
    testrun: {
      label: 'Test Run',
      id: 'testrun',
      key: 'isTestRun',
      icon: 'construction',
    },
    rollback: {
      label: 'Rollback',
      id: 'rollback',
      key: 'isRollback',
      icon: 'settings_backup_restore',
    },
  }

  export default {
    components: {
      VirtualList: virtualList,
      ImportCard,
      IngestionResultsLogDialog,
    },
    props: {},
    data() {
      return {
        dataImporters,
        statusTypes,
        statusTypeValues: Object.values(statusTypes),
        filters: {
          ingestion: [],
          testrun: [],
          rollback: [],
          services: [],
        },
        otherFilters: {
          latestVersionOnly: false,
          skipRolledBackIngestions: false,
        },
        queryFilters: {
          dateRange: {
            start: null,
            end: null,
          },
          searchText: '',
        },
        formats: {
          prettyDateFormat,
          prettyDateTimeFormatShort,
        },
        loading: {
          ingestionResults: false,
        },
        filteredImportIds: [],
        ignoreChange: false,
        // isIngestionFilter: false,
      }
    },
    computed: {
      ...ingestions.mapState(['importIds', 'importsIndex']),
      importCounts() {
        let importCounts = {}

        this.filteredImportIds.forEach(fid => {
          let _import = this.importsIndex[fid]
          for (let i = 0; i < this.statusTypeValues.length; i++) {
            let id = this.statusTypeValues[i].id
            let key = this.statusTypeValues[i].key

            if (_import[key]) {
              importCounts[id] = importCounts[id] ? importCounts[id] + 1 : 1
            }
          }
        })
        return importCounts
      },
      dateRangeShortcuts() {
        return {
          all: {
            hidden: true,
          },
        }
      },
      hasStatusFilters() {
        for (let i = 0; i < this.statusTypeValues.length; i++) {
          let id = this.statusTypeValues[i].id
          if (this.filters[id].length > 0) return true
        }
        return false
      },
      selectedServices: {
        get() {
          let selectedServices = []
          try {
            selectedServices = JSON.parse(this.$route.query.selectedServices)
          } catch (err) {
            selectedServices = []
          }
          return selectedServices
        },
        set(selectedServices) {
          this.$router.push({
            query: {
              ...this.$route.query,
              selectedServices: JSON.stringify(selectedServices),
            },
          })
        },
      },
      refreshResultsDebounced() {
        return debounce(this.refreshResults)
      },
      filterCurrentResultsDebounced() {
        return debounce(this.filterCurrentResults)
      },
      allFilters: {
        get() {
          let queryFilters = {
            ...this.queryFilters.dateRange,
          }

          let otherFilters = {
            ...this.otherFilters,
          }

          if (this.queryFilters.searchText) queryFilters.search = this.queryFilters.searchText


          let filters = {}
          Object.entries(this.filters).forEach(([key, values]) => {
            if (values.length > 0) filters[key] = values.join(',')
          })

          return {
            ...queryFilters,
            ...filters,
            ...otherFilters,
          }
        },
        set(query) {
          // sets the route URL params initially if there are no dates in the URL
          let startDate = moment().subtract(14, 'days').format('MMM DD YYYY')
          let endDate = moment().format('MMM DD YYYY')

          let queryFilters = {
            dateRange: {
              start: query.start || startDate,
              end: query.end || endDate,
            },
            searchText: query.search,
          }

          let otherFilters = {
            latestVersionOnly: !!query.latestVersionOnly,
          }

          let filters = {}
          Object.entries(this.filters).forEach(([key]) => {
            let queryVals = query[key]
            if (!queryVals) filters[key] = []
            else filters[key] = queryVals.split(',')
          })

          this.queryFilters = queryFilters
          this.filters = filters
          this.otherFilters = otherFilters
        },
      },
    },
    watch: {
      queryFilters: {
        deep: true,
        handler() {
          if (this.ignoreChange) return
          this.refreshResultsDebounced()
        },
      },
      otherFilters: {
        deep: true,
        handler() {
          if (this.ignoreChange) return
          this.refreshResultsDebounced()
        },
      },
      filters: {
        deep: true,
        handler() {
          if (this.ignoreChange) return
          this.updateRoute()
          this.loading.ingestionResults = true
          this.filterCurrentResultsDebounced()
        },
      },
      importIds: {
        handler() {
          if (this.ignoreChange) return
          this.loading.ingestionResults = true
          this.filterCurrentResultsDebounced()
        },
      },
    },
    methods: {
      ...ingestions.mapActions(['fetchIngestionResults']),
      pluralize,
      getMediaTypeIcon,
      updateRoute() {
        addParamsToLocation(this.allFilters, this.$route)
      },
      clearFilters() {
        this.updateFilters({})
      },
      onDatePresetSelected(presetName) {
        let dateRangeObj = this.dateRangeShortcuts[presetName]
        this.startDate = dateRangeObj.startDate
        this.endDate = dateRangeObj.endDate
      },
      filterCurrentResults() {
        let filteredImportIds = this.importIds

        if (this.hasStatusFilters) {
          filteredImportIds = filteredImportIds.filter(importId => {
            let _import = this.importsIndex[importId]
            for (let i = 0; i < this.statusTypeValues.length; i++) {
              let id = this.statusTypeValues[i].id
              let key = this.statusTypeValues[i].key
              let filterValues = this.filters[id]

              let canPass = !!filterValues.find(val => val == 'pass')
              let canFail = !!filterValues.find(val => val == 'fail')

              if (canPass && _import[key] && _import.isSuccess) return true
              else if (canFail && _import[key] && !_import.isSuccess) return true
            }

            return false
          })
        }

        if (this.filters.services.length > 0) {
          let servicesIndex = {}
          this.filters.services.forEach(s => servicesIndex[s] = true)
          filteredImportIds = filteredImportIds.filter(importId => {
            let _import = this.importsIndex[importId]
            return servicesIndex[_import.serviceLink]
          })
        }

        if (this.otherFilters.latestVersionOnly) {
          filteredImportIds = filteredImportIds.filter(importId => {
            let _import = this.importsIndex[importId]
            return _import.isLatestVersion
          })
        }

        if (this.otherFilters.skipRolledBackIngestions) {
          filteredImportIds = filteredImportIds.filter(importId => {
            let _import = this.importsIndex[importId]
            return !(_import.isIngestion && _import.rollbackDetail)
          })
        }

        this.filteredImportIds = filteredImportIds
        this.loading.ingestionResults = false
      },
      async refreshResults() {
        this.loading.ingestionResults = true
        this.updateRoute()

        try {
          let dateRange = this.queryFilters.dateRange

          let result = await this.fetchIngestionResults({
            searchText: this.queryFilters.searchText,
            startDate: moment(dateRange.start).format(backendDateTimeFormat),
            endDate: moment(dateRange.end).format(backendDateTimeFormat),
          })

          if (result.IsError) this.$refs.executionResultsDialog.show(result)
        } catch (error) {
          this.$refs.executionResultsDialog.show(error)
        }
      },
      showIngestionLogs(importId) {
        let item = this.importsIndex[importId]
        this.$refs.ingestionResultsDialog.show(item.executionId, item.isRollback)
      },
      async updateFilters(filters) {
        this.ignoreChange = true
        this.loading.ingestionResults = true
        await this.$nextTick()

        this.allFilters = filters
        this.updateRoute({})
        this.refreshResults()

        await this.$nextTick()
        this.ignoreChange = false
      },
    },
    async mounted() {
      this.updateFilters(this.$route.query)
    },
  }
</script>